// src/lib/customer-io.ts

import { createIsomorphicFn } from "@tanstack/react-start"
import type { AnalyticsBrowser as AnalyticsBrowserType } from "@customerio/cdp-analytics-browser"

type CustomerIO = ReturnType<typeof AnalyticsBrowserType.load>

let cio: CustomerIO | null = null
let loading = false
const queue: Array<(cio: CustomerIO) => void> = []

function flushQueue() {
  if (!cio) return
  while (queue.length) {
    queue.shift()?.(cio)
  }
}

function load() {
  if (loading || cio) return
  loading = true

  const writeKey = import.meta.env.VITE_CUSTOMER_IO_API_KEY

  if (!writeKey) {
    console.warn("Customer.io API key is missing")
    return
  }

  import("@customerio/cdp-analytics-browser").then(({ AnalyticsBrowser }) => {
    // Plain assignment — NOT `return`ed or `await`ed — so we keep the
    // synchronous buffered instance instead of its unwrapped `.then()` value.
    cio = AnalyticsBrowser.load({
      writeKey,

      // Only if your Customer.io workspace is EU:
      cdnURL: "https://cdp.customer.io",
    })

    flushQueue()
  })
}

function withCio(fn: (cio: CustomerIO) => void) {
  if (cio) {
    fn(cio)
  } else {
    queue.push(fn)
    load()
  }
}

export const initCustomerIO = createIsomorphicFn()
  .server(() => {})
  .client(() => {
    load()
  })

export const identifyCustomer = createIsomorphicFn()
  .server((_id: string | number, _traits?: Record<string, unknown>) => {})
  .client((id: string | number, traits?: Record<string, unknown>) => {
    withCio((cio) => cio.identify(String(id), traits))
  })

export const trackCustomerEvent = createIsomorphicFn()
  .server((_event: string, _properties?: Record<string, unknown>) => {})
  .client((event: string, properties?: Record<string, unknown>) => {
    withCio((cio) => cio.track(event, properties))
  })

export const trackCustomerPage = createIsomorphicFn()
  .server(() => {})
  .client((name?: string, properties?: Record<string, unknown>) => {
    withCio((cio) => {
      if (name) {
        cio.page(undefined, name, properties)
      } else {
        cio.page()
      }
    })
  })

export const resetCustomer = createIsomorphicFn()
  .server(() => {})
  .client(() => {
    withCio((cio) => cio.reset())
  })
